import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRefresh,
  faSearch,
  faFilter,
  faFileExcel,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import AbiBillingApiUrls from '../../../apiUrls/AbiBillingApiUrls';
import Spinner from '../../../components/era-package/era/Spinner';
import TextField from '../../../components/era-package/era/DebouncedTextField';
import Button from '../../../components/era-package/forms/Button';
import { Filter } from '../../../components/era-package/filters';

const columnHelper = createColumnHelper();

// ─── Excel export helper ────────────────────────────────────────────────────
const exportToExcel = (rows) => {
  const headers = [
    'Service',
    'User ID',
    'Transaction Date',
    'Filer Code',
    'Reference',
    'Ref No. 3',
    'Ref No. 4',
    'Message Type',
    'Transaction ID',
    'Status',
  ];

  const dataRows = rows.map((r) => [
    r.serviceName ?? '',
    r.nbsUserId ?? '',
    r.transactionDate ?? '',
    r.filerCode ?? '',
    r.reference ?? '',
    r.refNo3 ?? '',
    r.refNo4 ?? '',
    r.messageType ?? '',
    r.transactionId ?? '',
    r.status ?? '',
  ]);

  const xmlRows = [headers, ...dataRows]
    .map(
      (row) =>
        '<Row>' +
        row
          .map(
            (cell) =>
              `<Cell><Data ss:Type="String">${String(cell)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')}</Data></Cell>`
          )
          .join('') +
        '</Row>'
    )
    .join('');

  const xml = `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="Billing Report">
    <Table>${xmlRows}</Table>
  </Worksheet>
</Workbook>`;

  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'abi-billing-report.xls');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
// ────────────────────────────────────────────────────────────────────────────

const EMPTY_COL_PARAMS = {
  serviceName: '',
  nbsUserId: '',
  filerCode: '',
  reference: '',
  refNo3: '',
  refNo4: '',
  messageType: '',
  transactionId: '',
  status: '',
};

const BillingRecordList = () => {
  const theme = useTheme();

  const [data, setData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [transactionDateStart, setTransactionDateStart] = useState('');
  const [transactionDateEnd, setTransactionDateEnd] = useState('');
  const [columnParams, setColumnParams] = useState({ ...EMPTY_COL_PARAMS });
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const fetchData = useCallback(
    async (pageIndex, pageSize, search, dateStart, dateEnd, colParams) => {
      try {
        setIsRefreshing(true);
        const response = await fetch(AbiBillingApiUrls.GET, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')),
          },
          body: JSON.stringify({
            page: pageIndex,
            size: pageSize,
            ...(search && { search }),
            ...(dateStart && { transactionDateStart: dateStart }),
            ...(dateEnd && { transactionDateEnd: dateEnd }),
            // Only spread non-empty column filter params
            ...Object.fromEntries(
              Object.entries(colParams || {}).filter(([, v]) => v !== '')
            ),
          }),
        });
        const json = await response.json();
        setData(json.data?.content || json.content || []);
        setTotalElements(json.data?.totalElements || json.totalElements || 0);
      } catch (error) {
        console.error('Error fetching billing records:', error);
        setData([]);
        setTotalElements(0);
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchData(
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
      transactionDateStart,
      transactionDateEnd,
      columnParams
    );
  }, [
    fetchData,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    transactionDateStart,
    transactionDateEnd,
    columnParams,
  ]);

  const handleRefresh = useCallback(() => {
    fetchData(
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
      transactionDateStart,
      transactionDateEnd,
      columnParams
    );
  }, [fetchData, pagination.pageIndex, pagination.pageSize, globalFilter, transactionDateStart, transactionDateEnd, columnParams]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('serviceName', {
        header: 'Service',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('nbsUserId', {
        header: 'User ID',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('transactionDate', {
        header: 'Transaction Date',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
        meta: { filterVariant: 'dateRange' },
      }),
      columnHelper.accessor('filerCode', {
        header: 'Filer Code',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('reference', {
        header: 'Reference',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('refNo3', {
        header: 'Ref No. 3',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('refNo4', {
        header: 'Ref No. 4',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('messageType', {
        header: 'Message Type',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('transactionId', {
        header: 'Transaction ID',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => info.getValue() ?? '-',
        filterFn: 'includesString',
      }),
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualFiltering: true,
    pageCount: Math.ceil(totalElements / pagination.pageSize),
    onPaginationChange: setPagination,
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater;
      setColumnFilters(next);

      let dateStart = '';
      let dateEnd = '';
      const newColParams = { ...EMPTY_COL_PARAMS };

      next.forEach(({ id, value }) => {
        if (id === 'transactionDate' && Array.isArray(value)) {
          const [start, end] = value;
          dateStart = start && isValid(start) ? format(start, 'yyyy-MM-dd') : '';
          dateEnd = end && isValid(end) ? format(end, 'yyyy-MM-dd') : '';
        } else if (id in newColParams) {
          newColParams[id] = value ?? '';
        }
      });

      setTransactionDateStart(dateStart);
      setTransactionDateEnd(dateEnd);
      setColumnParams(newColParams);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
  });

  if (loading) {
    return (
      <div style={{ position: 'relative', height: '200px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={2}>
        {/* ── Search / Filter Controls ── */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            placeholder="Search all columns..."
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} width="14px" />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: { xs: '100%', sm: '300px' } }}
          />
          <Button
            startIcon={<FontAwesomeIcon icon={faRefresh} width="15" />}
            variant="outlined"
            onClick={handleRefresh}
            disabled={isRefreshing}
            color="success"
          >
            Refresh
          </Button>
          {isRefreshing && <CircularProgress size={20} />}
          <Button
            variant="outlined"
            onClick={() => setShowFilters((prev) => !prev)}
            startIcon={<FontAwesomeIcon icon={faFilter} style={{ width: 16, height: 16 }} />}
          >
            {showFilters ? 'Hide Filters' : 'Show Column Filters'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => exportToExcel(data)}
            disabled={data.length === 0}
            startIcon={<FontAwesomeIcon icon={faFileExcel} style={{ width: 16, height: 16 }} />}
          >
            Download Excel
          </Button>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  <TableRow>
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            backgroundColor: header.column.getCanSort()
                              ? theme.palette.action.hover
                              : 'transparent',
                          },
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() && (
                            <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                              {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                  {showFilters && (
                    <TableRow>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={`filter-${header.id}`}>
                          {header.column.getCanFilter() ? (
                            <Filter column={header.column} />
                          ) : null}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} hover>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} sx={{ whiteSpace: 'nowrap' }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Typography variant="body2" color={theme.palette.info.main}>
                      No billing records found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalElements > 0 && (
          <Box
            width="100%"
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            alignSelf="flex-end"
            justifyContent="space-between"
            alignItems="center"
            gap={{ xs: 0, md: 2 }}
          >
            <Typography variant="h6">Total {totalElements} Records</Typography>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalElements}
              rowsPerPage={pagination.pageSize}
              page={pagination.pageIndex}
              onPageChange={(event, newPage) => {
                setPagination((prev) => ({ ...prev, pageIndex: newPage }));
              }}
              onRowsPerPageChange={(event) => {
                setPagination((prev) => ({
                  ...prev,
                  pageSize: parseInt(event.target.value, 10),
                  pageIndex: 0,
                }));
              }}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
              sx={{ mt: '0!important' }}
            />
          </Box>
        )}
      </Stack>
    </LocalizationProvider>
  );
};

export default BillingRecordList;